import { mapGetters } from 'vuex'
import httpService from '@/core/http-service'
import gtm from '@/core/enhanced-ecommerce-gtm'
import _ from 'lodash'
import { state as filterState, storeFilters, getCachedLatestFilters, allFilterParameters } from '../store/filters'

const mixin = {
  data: () => ({
    searchQuery: '',
    pendingCancellations: [],
    entities: {
      categories: { data: [], page: 1, pageSize: 10, totalCount: null },
      items: { data: [], page: 1, pageSize: 10, totalCount: null, didYouMean: null },
      models: { data: [], page: 1, pageSize: 10, totalCount: null }
    },
    searchType: ''
  }),
  computed: {
    ...mapGetters(['enableGtm']),
    query() { return this.clientModel.searchQuery || this.searchQuery }
  },
  methods: {
    updateEntity(type, data) {
      const entityData = this.entities[type]
      this.clearSearchResult(entityData)
      _.forEach(data.data, result => {
        entityData.data.push(result)
      })
      entityData.extraResults = data.extraResults
      entityData.totalCount = data.totalCount
      entityData.didYouMean = data.didYouMean
    },
    createCancellation(type) {
      const cancel = httpService.createCancellationToken()
      _.forEach(this.pendingCancellations, cancellation => {
        if(cancellation.type === type) {
          cancellation.cancel()
        }
      })
      const cancellation = { type, cancel }
      return cancellation
    },
    async searchCategories() {
      const cancellation = this.createCancellation('categories')
      this.pendingCancellations.push(cancellation)

      try {
        const data = await httpService.get(`search/categories`, { page: this.entities.categories.page, pageSize: this.entities.categories.pageSize, query: this.query }, cancellation.cancel.cancelTokenSource)
        this.updateEntity('categories', data.data)
        if (this.enableGtm) {
          gtm.addSearchEvent(this.searchType, 'categories', this.query, data.totalCount)
        }
      } catch(e) {
        // No action?
      } finally {
        this.pendingCancellations.remove(cancellation)
      }
    },
    async searchModels() {
      const cancellation = this.createCancellation('models')
      this.pendingCancellations.push(cancellation)

      try {
        const data = await httpService.get(`search/models`, { page: this.entities.models.page, pageSize: this.entities.models.pageSize, query: this.query }, cancellation.cancel.cancelTokenSource)
        this.updateEntity('models', data.data)
        if (this.enableGtm) {
          gtm.addSearchEvent(this.searchType, 'models', this.query, data.totalCount)
        }
      } catch(e) {
        // No action?
      } finally {
        this.pendingCancellations.remove(cancellation)
      }
    },
    async searchItems(filterParameters = {}) {
      const cancellation = this.createCancellation('items')
      this.pendingCancellations.push(cancellation)

      const defaultParameters = {
        page: this.entities.items.page,
        pageSize: this.entities.items.pageSize,
        query: this.query
      }

      const parameters = { ...defaultParameters, ...filterParameters }

      try {
        const data = await httpService.post(`search/items`, parameters, cancellation.cancel.cancelTokenSource)
        this.updateEntity('items', data.data)
        if (this.enableGtm) {
          gtm.addSearchEvent(this.searchType, 'items', this.query, data.totalCount)
        }
      } catch(e) {
        // No action?
      } finally {
        this.pendingCancellations.remove(cancellation)
      }
    },
    searchAll: _.debounce(async function() {
      if (!this.query || this.query.length < 2) {
        return
      }
      await Promise.all([this.searchItems(), this.searchCategories(), this.searchModels()])
    }, 500, { leading: true }),
    async getFilters(filterParameters = {}) {
      filterState.fetchingFilters = true
      const cachedFilters = getCachedLatestFilters(this.query, filterParameters)
      if(cachedFilters) {
        filterState.fetchingFilters = false
        return cachedFilters
      } else {
        const facets = await httpService.post('search/items',
          {
            page: this.entities.items.page,
            pageSize: 0,
            query: this.query,
            includeFacets: true,
            ...filterParameters
          })
        sessionStorage.setItem('latestFilters', JSON.stringify({ filters: filterParameters, key: this.query, data: facets.data }))
        filterState.fetchingFilters = false
        return facets.data
      }
    },
    async updateItemsAndFilters() {
      this.searchItems(allFilterParameters())
      const filters = await this.getFilters(allFilterParameters())
      storeFilters(filters)
    },
    clearSearchResults() {
      _.forEach(this.entities, this.clearSearchResult)
    },
    clearSearchResult(entity) {
      entity.data = []
      entity.totalCount = null
    }
  }

}

export default mixin
